import {
  faMagnifyingGlass,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "../../GenericTable.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SearchQueryEvent } from "../../Models/searchQueryEvent";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";

// Search feedback (flag logoSearchFeedbackEnabled; see
// useLogoSearchFeedback). Nothing is fetched here - the caller passes the
// react-query state of the list behind the field.
export interface SearchActivity {
  // A search-driven fetch is in flight for the term last emitted.
  isSearching: boolean;
  // The debounce window opened with this draft text / closed (null), so the
  // caller can say "Searching for '<draft>'…" before the term is emitted.
  onDraftChange?: (draft: string | null) => void;
}

export interface ListSearchBarProps {
  onSearchQueryChange: (nextSearchQuery: SearchQueryEvent) => void | undefined;
  searchQuery?: string;
  border?: boolean;
  grayBG?: boolean;
  // When given, a spinner sits at the right edge of the field from the first
  // keystroke (the 350 ms debounce window included) until isSearching is
  // false again. Omitted = today's DOM, byte for byte.
  activity?: SearchActivity;
}

function TableSearchBar({
  onSearchQueryChange,
  searchQuery,
  border = false,
  grayBG = false,
  activity,
}: ListSearchBarProps) {
  const { t } = useTranslation();
  // Controlled input, initialised from the active query so the field still
  // shows the current search term after navigating back to the list (the
  // term round-trips through the URL via usePageQuery -> ListHeader).
  const [value, setValue] = useState<string>(searchQuery ?? "");

  // Last search term actually emitted (always trimmed). Serves two purposes:
  // - skip emits whose trimmed value is unchanged (no redundant refetches,
  //   e.g. trailing spaces), while an intentional clear ("") still fires;
  // - tell *external* query changes (back/forward navigation) apart from the
  //   echo of our own emit coming back down as a prop, so typing is never
  //   clobbered mid-word.
  const lastEmittedRef = useRef<string>((searchQuery ?? "").trim());

  // Draft tracking is armed only while `activity` is given; read through a
  // ref so the memoised debounce below never has to be rebuilt for it.
  const trackDraft = activity !== undefined;
  const trackDraftRef = useRef(trackDraft);
  trackDraftRef.current = trackDraft;
  // Text typed but not yet emitted (the debounce window); null = idle.
  const [draft, setDraft] = useState<string | null>(null);
  const onDraftChangeRef = useRef(activity?.onDraftChange);
  onDraftChangeRef.current = activity?.onDraftChange;
  useEffect(() => {
    onDraftChangeRef.current?.(draft);
  }, [draft]);

  // Create a debounced version of the search callback
  const debouncedSearch = useMemo(
    () =>
      debounce((rawTerm: string) => {
        // The window closes whether or not a term is emitted below; the
        // list's own fetch state takes over from here (activity.isSearching).
        if (trackDraftRef.current) setDraft(null);
        const searchTerm = rawTerm.trim();
        if (searchTerm === lastEmittedRef.current) {
          return;
        }
        lastEmittedRef.current = searchTerm;
        onSearchQueryChange({ searchTerm });
      }, 350),
    [onSearchQueryChange]
  );

  // Sync from an external query change (e.g. browser back/forward). Our own
  // emits come back as a prop equal to lastEmittedRef and are ignored.
  useEffect(() => {
    const external = searchQuery ?? "";
    if (external.trim() !== lastEmittedRef.current) {
      lastEmittedRef.current = external.trim();
      setValue(external);
      debouncedSearch.cancel();
      if (trackDraftRef.current) setDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    if (trackDraft) {
      // Only a change the debounce will actually emit opens the window (a
      // trailing space on the emitted term does not).
      setDraft(newValue.trim() !== lastEmittedRef.current ? newValue : null);
    }
    debouncedSearch(newValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Search immediately instead of waiting out the debounce delay.
      debouncedSearch.flush();
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div
      className={`w-full text-xs rounded-md h-9 flex space-x-2 items-center pl-2 hover:cursor-text ${
        border ? "border border-borderGray" : ""
      } ${grayBG ? "bg-gray-100 hover:bg-white" : "bg-white"}`}
    >
      <span>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </span>
      <input
        placeholder={t("search")}
        className="w-full h-full border-none text-gray-600 text-xs font-medium outline-none pr-4 bg-transparent"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {activity && (draft !== null || activity.isSearching) && (
        <span
          className="flex items-center pr-3 text-gray-500"
          data-testid="search-activity-spinner"
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        </span>
      )}
    </div>
  );
}

export default TableSearchBar;
